import { useEffect, useMemo, useState } from "react";
import { api, AUD, PCT } from "../lib/api";
import { toast } from "sonner";
import {
    Coins, Lock, LockOpen, Warning, TrendUp, ArrowClockwise,
    FloppyDisk, Trash, ChartLine, X,
} from "@phosphor-icons/react";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from "recharts";

/**
 * Cash Plan — annual cash flow planner.
 *
 * Modes:
 *  - Working              → salary + Pendaz + personal only
 *  - Transition to Retire → salary + statutory SMSF pension
 *  - Full Retirement      → SMSF pension + Pendaz + personal (no salary)
 *
 * Surplus → NCC into SMSF (balance-weighted split, $120K/yr per member cap).
 * Timeline projects gap year-by-year to age 95.
 */

function statutoryPct(age) {
    if (age < 65) return 0.04;
    if (age < 75) return 0.05;
    if (age < 80) return 0.06;
    if (age < 85) return 0.07;
    if (age < 90) return 0.09;
    if (age < 95) return 0.11;
    return 0.14;
}

const MODES = [
    { key: "working",    label: "Working",              hint: "Salary + Pendaz + personal only" },
    { key: "transition", label: "Transition to Retire", hint: "Salary + statutory SMSF pension" },
    { key: "retired",    label: "Full Retirement",      hint: "Pension + Pendaz + personal (no salary)" },
];

const NCC_CAP_PER_MEMBER = 120000;

const asmValue = (list, key, fallback) => {
    const row = (list || []).find(a => a.item === key);
    return row ? Number(row.value) : fallback;
};

export default function CashPlan() {
    const [mode, setMode] = useState("transition");
    const [smsf, setSmsf] = useState(null);
    const [lifestyleAnnual, setLifestyleAnnual] = useState(0);
    const [assumptions, setAssumptions] = useState([]);
    const [scenarios, setScenarios] = useState([]);
    const [loaded, setLoaded] = useState(false);

    const [inputs, setInputs] = useState({
        darren_age: 58,
        penny_age: 55,
        darren_retire_age: 65,
        pension_pct_darren: null,
        pension_pct_penny:  null,
        salary_net: 165000,
        directors_fee_darren: 25000,
        directors_fee_penny:  20000,
        pendaz_dividends: 8000,
        personal_dividends: 12000,
        lifestyle_target_override: null,
    });
    const [locked, setLocked] = useState({});
    const [showTimeline, setShowTimeline] = useState(false);
    const [savePanelOpen, setSavePanelOpen] = useState(false);
    const [scenarioName, setScenarioName] = useState("");

    const load = async () => {
        try {
            const [s, l, a, sc] = await Promise.all([
                api.get("/smsf").then(r => r.data),
                api.get("/lifestyle").then(r => r.data),
                api.get("/assumptions").then(r => r.data),
                api.get("/cash-plan/scenarios").then(r => r.data).catch(() => []),
            ]);
            setSmsf(s);
            setLifestyleAnnual((l || []).reduce((a, c) => a + (Number(c.annual_budget) || 0), 0));
            setAssumptions(a || []);
            setScenarios(sc || []);
            // Seed ages / retire age from Assumptions when available so
            // "single source of truth" wins over hard-coded defaults.
            setInputs(prev => ({
                ...prev,
                darren_age:        asmValue(a, "Darren Age",        prev.darren_age),
                penny_age:         asmValue(a, "Penny Age",         prev.penny_age),
                darren_retire_age: asmValue(a, "Darren Retire Age", prev.darren_retire_age),
                salary_net:        asmValue(a, "Darren Salary FY27", prev.salary_net) * 0.66, // rough net
            }));
            setLoaded(true);
        } catch {
            toast.error("Couldn't load Cash Plan data");
        }
    };
    useEffect(() => { load(); }, []);

    const darrenBal = smsf?.members?.find(m => m.name === "Darren")?.value || 0;
    const pennyBal  = smsf?.members?.find(m => m.name === "Penny")?.value  || 0;
    const totalMemberBal = darrenBal + pennyBal;

    const darrenPct = inputs.pension_pct_darren ?? statutoryPct(inputs.darren_age);
    const pennyPct  = inputs.pension_pct_penny  ?? statutoryPct(inputs.penny_age);
    const darrenPension = mode === "working" ? 0 : darrenBal * darrenPct;
    const pennyPension  = mode === "working" ? 0 : pennyBal  * pennyPct;

    const lifestyleTarget = inputs.lifestyle_target_override ?? lifestyleAnnual;
    const totalDirFees = (inputs.directors_fee_darren || 0) + (inputs.directors_fee_penny || 0);

    const sources = useMemo(() => {
        const list = [];
        if (mode !== "retired") {
            list.push({ key: "salary_net", label: "Darren salary (net)", value: inputs.salary_net, color: "#5F8071" });
        }
        if (mode !== "working") {
            list.push({ key: "pension_darren", label: `SMSF pension — Darren (${(darrenPct*100).toFixed(1)}%)`, value: darrenPension, color: "#4A6B7A", readOnly: true });
            list.push({ key: "pension_penny",  label: `SMSF pension — Penny (${(pennyPct*100).toFixed(1)}%)`,  value: pennyPension,  color: "#4A6B7A", readOnly: true });
        }
        list.push({ key: "dir_darren", label: "Director's fees — Darren", value: inputs.directors_fee_darren, color: "#8C6E52" });
        list.push({ key: "dir_penny",  label: "Director's fees — Penny",  value: inputs.directors_fee_penny,  color: "#A88F6E" });
        list.push({ key: "pendaz_dividends",   label: "Pendaz dividends (personal)", value: inputs.pendaz_dividends, color: "#B7A88F" });
        list.push({ key: "personal_dividends", label: "Personal dividends & rent",   value: inputs.personal_dividends, color: "#7B8F5C" });
        return list;
    }, [mode, inputs, darrenPension, pennyPension, darrenPct, pennyPct]);

    const totalSources = sources.reduce((a, s) => a + (s.value || 0), 0);
    const gap = totalSources - lifestyleTarget;
    const inSurplus = gap >= 0;

    const nccSurplus = Math.max(0, gap);
    const nccDarrenSuggested = totalMemberBal ? nccSurplus * (darrenBal / totalMemberBal) : nccSurplus / 2;
    const nccPennySuggested  = totalMemberBal ? nccSurplus * (pennyBal  / totalMemberBal) : nccSurplus / 2;
    const nccDarrenCapped = Math.min(nccDarrenSuggested, NCC_CAP_PER_MEMBER);
    const nccPennyCapped  = Math.min(nccPennySuggested,  NCC_CAP_PER_MEMBER);
    const overCap = (nccDarrenSuggested + nccPennySuggested) > (nccDarrenCapped + nccPennyCapped);

    // Timeline projection — year-by-year to age 95 for Darren
    const timeline = useMemo(() => {
        const inflation = asmValue(assumptions, "Inflation", 0.03);
        const ret = asmValue(assumptions, "Expected Portfolio Return", 0.06);
        const endAge = asmValue(assumptions, "Planning Age", 95);
        const years = Math.max(1, endAge - inputs.darren_age);
        let dBal = darrenBal;
        let pBal = pennyBal;
        const rows = [];
        for (let t = 0; t <= years; t++) {
            const dAge = inputs.darren_age + t;
            const pAge = inputs.penny_age  + t;
            const stillWorking = dAge < inputs.darren_retire_age;
            const yearMode = stillWorking ? "transition" : "retired";
            const dPct = statutoryPct(dAge);
            const pPct = statutoryPct(pAge);
            const dPen = yearMode === "working" ? 0 : dBal * dPct;
            const pPen = yearMode === "working" ? 0 : pBal * pPct;
            const infl = Math.pow(1 + inflation, t);
            const salary = stillWorking ? inputs.salary_net : 0;
            const nonSuper =
                (inputs.directors_fee_darren + inputs.directors_fee_penny +
                 inputs.pendaz_dividends + inputs.personal_dividends) * infl;
            const src = salary + dPen + pPen + nonSuper;
            const life = lifestyleTarget * infl;
            const yearGap = src - life;
            rows.push({
                age: dAge,
                sources: Math.round(src),
                lifestyle: Math.round(life),
                gap: Math.round(yearGap),
                darren_bal: Math.round(dBal),
                penny_bal:  Math.round(pBal),
                combined_bal: Math.round(dBal + pBal),
            });
            // Advance balances: growth on remaining balance after pension paid.
            // Any surplus recycled as NCC (capped) gets added back.
            const nccBack = Math.min(Math.max(0, yearGap), NCC_CAP_PER_MEMBER * 2);
            const nccD = totalMemberBal ? nccBack * (dBal / (dBal + pBal || 1)) : nccBack / 2;
            const nccP = nccBack - nccD;
            dBal = Math.max(0, (dBal - dPen) * (1 + ret) + nccD);
            pBal = Math.max(0, (pBal - pPen) * (1 + ret) + nccP);
        }
        return rows;
    }, [inputs, assumptions, darrenBal, pennyBal, lifestyleTarget, totalMemberBal]);

    const set = (k, v) => setInputs(s => ({ ...s, [k]: v }));
    const toggleLock = (k) => setLocked(s => ({ ...s, [k]: !s[k] }));

    const saveScenario = async () => {
        const name = scenarioName.trim();
        if (!name) return toast.error("Give the scenario a name");
        try {
            const { data } = await api.post("/cash-plan/scenarios", {
                name, mode, inputs,
            });
            setScenarios([...scenarios, data]);
            setScenarioName("");
            setSavePanelOpen(false);
            toast.success(`Saved "${name}"`);
        } catch {
            toast.error("Save failed");
        }
    };
    const loadScenario = (sc) => {
        setMode(sc.mode || "transition");
        setInputs(sc.inputs || {});
        toast.success(`Loaded "${sc.name}"`);
    };
    const deleteScenario = async (sc) => {
        if (!window.confirm(`Delete "${sc.name}"?`)) return;
        try {
            await api.delete(`/cash-plan/scenarios/${sc.id}`);
            setScenarios(scenarios.filter(x => x.id !== sc.id));
            toast.success("Deleted");
        } catch {
            toast.error("Delete failed");
        }
    };

    if (!loaded) return <div className="text-[var(--muted)]">Loading…</div>;

    return (
        <div data-testid="cash-plan">
            {/* Mode toggle + tools */}
            <div className="hairline bg-white p-4 rounded mb-6 flex items-center justify-between flex-wrap gap-3"
                 data-testid="cash-plan-mode">
                <div className="inline-flex rounded border border-[var(--line)] overflow-hidden text-sm">
                    {MODES.map(m => (
                        <button key={m.key} onClick={() => setMode(m.key)}
                                data-testid={`cash-plan-mode-${m.key}`}
                                className={`px-4 py-2 transition-colors ${
                                    mode === m.key ? "bg-[var(--eucalyptus)] text-[var(--bone)]"
                                                   : "hover:bg-black/5"}`}>
                            {m.label}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2 text-xs text-[var(--muted)] flex-1 min-w-[220px] justify-end">
                    <span className="hidden md:inline">
                        {MODES.find(m => m.key === mode)?.hint} · SMSF{" "}
                        <span className="font-mono-num text-[var(--ink)]">{AUD((smsf?.total_value) || 0)}</span>
                    </span>
                    <button onClick={() => setShowTimeline(!showTimeline)}
                            data-testid="toggle-timeline"
                            className={`btn-ghost text-xs flex items-center gap-1 ${
                                showTimeline ? "text-[var(--eucalyptus)]" : ""}`}>
                        <ChartLine size={12} weight={showTimeline ? "fill" : "regular"} />
                        Timeline
                    </button>
                    <button onClick={() => setSavePanelOpen(!savePanelOpen)}
                            data-testid="toggle-save"
                            className="btn-ghost text-xs flex items-center gap-1">
                        <FloppyDisk size={12} /> Scenarios ({scenarios.length})
                    </button>
                </div>
            </div>

            {/* Scenarios panel */}
            {savePanelOpen && (
                <div className="hairline bg-white p-6 rounded mb-6" data-testid="scenarios-panel">
                    <div className="flex items-center justify-between mb-4">
                        <div className="label-eyebrow">Saved scenarios</div>
                        <button onClick={() => setSavePanelOpen(false)}
                                className="text-[var(--muted)] hover:text-[var(--ink)]"
                                data-testid="scenarios-close">
                            <X size={16} />
                        </button>
                    </div>
                    <div className="flex gap-2 mb-4">
                        <input type="text" value={scenarioName}
                               onChange={e => setScenarioName(e.target.value)}
                               placeholder={`e.g. "TTR at 60 · 4% pension · $180K lifestyle"`}
                               data-testid="scenario-name-input"
                               className="flex-1 border border-[var(--line)] px-3 py-2 rounded text-sm" />
                        <button onClick={saveScenario} data-testid="scenario-save-btn"
                                className="btn-primary text-sm inline-flex items-center gap-1">
                            <FloppyDisk size={12} /> Save current
                        </button>
                    </div>
                    {scenarios.length === 0 ? (
                        <div className="text-xs text-[var(--muted)]">
                            No saved scenarios yet. Tweak the inputs, name it and hit Save.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {scenarios.map(sc => (
                                <div key={sc.id} data-testid={`scenario-row-${sc.id}`}
                                     className="flex items-center justify-between py-2 border-b border-[var(--line)] last:border-none text-sm">
                                    <div className="flex-1">
                                        <div className="font-medium">{sc.name}</div>
                                        <div className="text-xs text-[var(--muted)]">
                                            {sc.mode} · saved {new Date(sc.created_at).toLocaleDateString("en-AU")}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => loadScenario(sc)} className="btn-ghost text-xs"
                                                data-testid={`scenario-load-${sc.id}`}>Load</button>
                                        <button onClick={() => deleteScenario(sc)}
                                                className="text-[var(--muted)] hover:text-[var(--terracotta)]"
                                                data-testid={`scenario-del-${sc.id}`}>
                                            <Trash size={14} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* KPI row */}
            <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="hairline bg-white p-6 rounded" data-testid="kpi-target">
                    <div className="label-eyebrow flex items-center gap-2"><Coins size={12} /> Lifestyle target</div>
                    <div className="font-serif text-3xl mt-2 font-mono-num">{AUD(lifestyleTarget)}</div>
                    <div className="text-xs text-[var(--muted)] mt-1">
                        {AUD(lifestyleTarget / 12)}/mo · from Lifestyle module
                    </div>
                </div>
                <div className="hairline bg-white p-6 rounded" data-testid="kpi-sources">
                    <div className="label-eyebrow flex items-center gap-2"><TrendUp size={12} /> Total sources</div>
                    <div className="font-serif text-3xl mt-2 font-mono-num">{AUD(totalSources)}</div>
                    <div className="text-xs text-[var(--muted)] mt-1">Annualised · pre-tax on non-super income</div>
                </div>
                <div className="p-6 rounded" data-testid="kpi-gap"
                     style={{
                         background: inSurplus ? "var(--eucalyptus)" : "var(--terracotta)",
                         color: "var(--bone)",
                     }}>
                    <div className="text-[10px] uppercase tracking-widest opacity-80">
                        {inSurplus ? "Surplus — recycle to SMSF" : "Shortfall — top up sources"}
                    </div>
                    <div className="font-serif text-3xl mt-2 font-mono-num">
                        {inSurplus ? "+" : ""}{AUD(gap)}
                    </div>
                    <div className="text-xs mt-1 opacity-85">
                        {inSurplus
                            ? `Suggest NCC: ${AUD(nccDarrenCapped)} Darren + ${AUD(nccPennyCapped)} Penny`
                            : "Bump pension % or director's fees to cover the gap"}
                    </div>
                </div>
            </div>

            {/* Fill-up bar */}
            <div className="hairline bg-white p-6 rounded mb-6" data-testid="cash-plan-bar">
                <div className="flex items-baseline justify-between mb-4">
                    <div>
                        <div className="label-eyebrow">Fill-up view</div>
                        <h2 className="font-serif text-xl mt-1">Sources filling your lifestyle target</h2>
                    </div>
                    <div className="text-xs text-[var(--muted)]">
                        {PCT(Math.min(1, totalSources / (lifestyleTarget || 1)), 0)} of target covered
                    </div>
                </div>
                <div className="relative h-10 rounded overflow-hidden mb-2"
                     style={{ background: "rgba(0,0,0,0.06)" }}>
                    {(() => {
                        const denom = Math.max(lifestyleTarget, totalSources, 1);
                        let x = 0;
                        return sources.filter(s => s.value > 0).map(s => {
                            const w = (s.value / denom) * 100;
                            const el = (
                                <div key={s.key} title={`${s.label}: ${AUD(s.value)}`}
                                     style={{
                                         position: "absolute", top: 0, bottom: 0,
                                         left: `${x}%`, width: `${w}%`,
                                         background: s.color, opacity: 0.9,
                                         borderRight: "1px solid rgba(255,255,255,0.35)",
                                     }} />
                            );
                            x += w;
                            return el;
                        });
                    })()}
                    <div style={{
                        position: "absolute", top: -4, bottom: -4,
                        left: `${Math.min(100, (lifestyleTarget / Math.max(lifestyleTarget, totalSources, 1)) * 100)}%`,
                        width: 2, background: "var(--ink)",
                    }} />
                </div>
                <div className="flex justify-between text-[10px] uppercase tracking-widest text-[var(--muted)]">
                    <span>$0</span>
                    <span>Target {AUD(lifestyleTarget)} ↑</span>
                    <span>Sources {AUD(totalSources)}</span>
                </div>
                <div className="grid md:grid-cols-2 gap-2 mt-5">
                    {sources.map(s => (
                        <div key={s.key} className="flex items-center gap-3 text-sm py-1">
                            <span className="inline-block w-3 h-3 rounded" style={{ background: s.color }} />
                            <span className="flex-1 text-[var(--muted)]">{s.label}</span>
                            <span className="font-mono-num">{AUD(s.value)}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Editable inputs */}
            <div className="hairline bg-white p-6 rounded mb-6" data-testid="cash-plan-inputs">
                <div className="flex items-center justify-between mb-4">
                    <div className="label-eyebrow">Adjust the assumptions</div>
                    <button className="text-xs text-[var(--eucalyptus)] hover:underline"
                            data-testid="cash-plan-reset" onClick={() => load()}>
                        <ArrowClockwise size={12} className="inline mr-1" />
                        Re-sync from Assumptions & SMSF
                    </button>
                </div>
                <div className="grid md:grid-cols-4 gap-4">
                    <AgeField label="Darren age" value={inputs.darren_age}
                              onChange={v => set("darren_age", v)} tid="input-darren-age" />
                    <AgeField label="Penny age" value={inputs.penny_age}
                              onChange={v => set("penny_age", v)} tid="input-penny-age" />
                    <AgeField label="Darren retire age" value={inputs.darren_retire_age}
                              onChange={v => set("darren_retire_age", v)} tid="input-darren-retire" />
                    <div />
                    <PctField label={`Darren pension % (min ${(statutoryPct(inputs.darren_age)*100).toFixed(0)}%)`}
                              value={inputs.pension_pct_darren ?? statutoryPct(inputs.darren_age)}
                              onChange={v => set("pension_pct_darren", v)}
                              onReset={() => set("pension_pct_darren", null)}
                              isOverride={inputs.pension_pct_darren !== null}
                              tid="input-pension-darren" />
                    <PctField label={`Penny pension % (min ${(statutoryPct(inputs.penny_age)*100).toFixed(0)}%)`}
                              value={inputs.pension_pct_penny ?? statutoryPct(inputs.penny_age)}
                              onChange={v => set("pension_pct_penny", v)}
                              onReset={() => set("pension_pct_penny", null)}
                              isOverride={inputs.pension_pct_penny !== null}
                              tid="input-pension-penny" />
                    <DollarField label="Darren salary (net)"
                                 value={inputs.salary_net} onChange={v => set("salary_net", v)}
                                 locked={locked.salary_net} onLock={() => toggleLock("salary_net")}
                                 disabled={mode === "retired"} tid="input-salary" />
                    <div />
                    <DollarField label="Director's fees — Darren"
                                 value={inputs.directors_fee_darren}
                                 onChange={v => set("directors_fee_darren", v)}
                                 locked={locked.directors_fee_darren}
                                 onLock={() => toggleLock("directors_fee_darren")}
                                 tid="input-dir-darren" />
                    <DollarField label="Director's fees — Penny"
                                 value={inputs.directors_fee_penny}
                                 onChange={v => set("directors_fee_penny", v)}
                                 locked={locked.directors_fee_penny}
                                 onLock={() => toggleLock("directors_fee_penny")}
                                 tid="input-dir-penny" />
                    <DollarField label="Pendaz dividends (personal)"
                                 value={inputs.pendaz_dividends} onChange={v => set("pendaz_dividends", v)}
                                 locked={locked.pendaz_dividends} onLock={() => toggleLock("pendaz_dividends")}
                                 tid="input-pendaz-div" />
                    <DollarField label="Personal dividends & rent"
                                 value={inputs.personal_dividends} onChange={v => set("personal_dividends", v)}
                                 locked={locked.personal_dividends} onLock={() => toggleLock("personal_dividends")}
                                 tid="input-personal-div" />
                </div>
                <div className="mt-4 max-w-md">
                    <DollarField label="Lifestyle target (override)"
                                 placeholder={`Auto from Lifestyle (${AUD(lifestyleAnnual)})`}
                                 value={inputs.lifestyle_target_override ?? ""}
                                 onChange={v => set("lifestyle_target_override", v === "" ? null : v)}
                                 onReset={() => set("lifestyle_target_override", null)}
                                 isOverride={inputs.lifestyle_target_override !== null}
                                 tid="input-lifestyle-override" />
                </div>
                <div className="text-[11px] text-[var(--muted)] mt-4">
                    Combined director's fees: <span className="font-mono-num text-[var(--ink)]">{AUD(totalDirFees)}</span> ·
                    Ages & retire age auto-loaded from the Assumptions page — change there for permanent, change here for what-if.
                </div>
            </div>

            {/* NCC recommendation */}
            {inSurplus && nccSurplus > 0 && (
                <div className="hairline bg-white p-6 rounded mb-6" data-testid="ncc-panel">
                    <div className="label-eyebrow flex items-center gap-2 mb-3">
                        <Coins size={12} />
                        Recycle surplus → SMSF (non-concessional contributions)
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <NccRow name="Darren" bal={darrenBal} suggested={nccDarrenSuggested}
                                capped={nccDarrenCapped} totalBal={totalMemberBal} tid="ncc-darren" />
                        <NccRow name="Penny"  bal={pennyBal}  suggested={nccPennySuggested}
                                capped={nccPennyCapped}  totalBal={totalMemberBal} tid="ncc-penny" />
                    </div>
                    {overCap && (
                        <div className="mt-4 text-xs text-[var(--terracotta)] flex items-start gap-2">
                            <Warning size={14} weight="fill" />
                            <span>
                                Suggested contributions exceed the $120K/yr per-member cap.
                                Consider bring-forward (up to $360K/3yrs each) or
                                park the excess in the personal offset until next FY.
                                <br />
                                <em>Note:</em> your handbook says bring-forward is currently maxed through FY28 —
                                the numbers above are informational only.
                            </span>
                        </div>
                    )}
                </div>
            )}

            {!inSurplus && (
                <div className="p-6 rounded border text-sm"
                     style={{ borderColor: "var(--terracotta)", background: "rgba(201,123,90,0.08)" }}
                     data-testid="shortfall-panel">
                    <div className="flex items-center gap-2 mb-2">
                        <Warning size={16} weight="fill" className="text-[var(--terracotta)]" />
                        <span className="label-eyebrow text-[var(--terracotta)]">Cash shortfall — {AUD(Math.abs(gap))}/yr</span>
                    </div>
                    <div className="text-[var(--ink)] leading-relaxed">
                        Your current sources cover {PCT(totalSources / (lifestyleTarget || 1), 0)} of the lifestyle target.
                        Options: lift the SMSF pension % (each additional 1% ≈ {AUD((darrenBal + pennyBal) * 0.01)}/yr),
                        raise director's fees from Pendaz, or trim the lifestyle target.
                    </div>
                </div>
            )}

            {/* Timeline */}
            {showTimeline && (
                <div className="hairline bg-white p-6 rounded mt-6" data-testid="timeline-panel">
                    <div className="flex items-baseline justify-between mb-4">
                        <div>
                            <div className="label-eyebrow flex items-center gap-2"><ChartLine size={12} /> Timeline projection</div>
                            <h2 className="font-serif text-xl mt-1">
                                Sources vs lifestyle · Darren age {inputs.darren_age} → {asmValue(assumptions, "Planning Age", 95)}
                            </h2>
                            <p className="text-xs mt-1 text-[var(--muted)]">
                                Auto-switches to Full Retirement at age {inputs.darren_retire_age}. Balances grow at Expected Portfolio Return
                                ({PCT(asmValue(assumptions, "Expected Portfolio Return", 0.06), 1)}); lifestyle indexes to inflation
                                ({PCT(asmValue(assumptions, "Inflation", 0.03), 1)}). Any surplus recycled to SMSF at $120K/yr cap each.
                            </p>
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height={340}>
                        <LineChart data={timeline} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                            <XAxis dataKey="age" fontSize={11} stroke="var(--muted)" />
                            <YAxis fontSize={11} stroke="var(--muted)"
                                   tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                            <Tooltip formatter={(v) => AUD(v)}
                                     labelFormatter={a => `Age ${a}`}
                                     contentStyle={{ fontSize: 12, borderRadius: 4 }} />
                            <Legend fontSize={11} />
                            <ReferenceLine x={inputs.darren_retire_age} stroke="#C97B5A"
                                           strokeDasharray="4 4"
                                           label={{ value: "Retire", fontSize: 10, fill: "#C97B5A" }} />
                            <Line type="monotone" dataKey="lifestyle" stroke="#C97B5A" strokeWidth={2}
                                  name="Lifestyle" dot={false} />
                            <Line type="monotone" dataKey="sources" stroke="#5F8071" strokeWidth={2}
                                  name="Sources" dot={false} />
                            <Line type="monotone" dataKey="gap" stroke="#4A6B7A" strokeWidth={2}
                                  name="Gap (surplus/shortfall)" dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                    <div className="mt-4 text-xs text-[var(--muted)]">
                        Balance at age {asmValue(assumptions, "Planning Age", 95)}: Darren{" "}
                        <span className="font-mono-num text-[var(--ink)]">{AUD(timeline[timeline.length-1]?.darren_bal || 0)}</span> ·
                        Penny <span className="font-mono-num text-[var(--ink)]">{AUD(timeline[timeline.length-1]?.penny_bal || 0)}</span> ·
                        Combined <span className="font-mono-num text-[var(--ink)]">{AUD(timeline[timeline.length-1]?.combined_bal || 0)}</span>
                    </div>
                </div>
            )}
        </div>
    );
}

function AgeField({ label, value, onChange, tid }) {
    return (
        <label className="block">
            <div className="text-xs text-[var(--muted)] uppercase tracking-wider mb-1">{label}</div>
            <input type="number" min={30} max={100} value={value}
                   onChange={e => onChange(Number(e.target.value) || 0)}
                   data-testid={tid}
                   className="w-full border border-[var(--line)] px-3 py-2 rounded text-sm font-mono-num" />
        </label>
    );
}

function PctField({ label, value, onChange, onReset, isOverride, tid }) {
    return (
        <label className="block">
            <div className="text-xs text-[var(--muted)] uppercase tracking-wider mb-1 flex justify-between items-center">
                <span>{label}</span>
                {isOverride && (
                    <button onClick={onReset} className="text-[10px] text-[var(--eucalyptus)] hover:underline">
                        auto
                    </button>
                )}
            </div>
            <div className="flex items-center gap-1">
                <input type="number" min={0.04} max={0.20} step={0.005} value={value}
                       onChange={e => onChange(Number(e.target.value) || 0)}
                       data-testid={tid}
                       className="w-full border border-[var(--line)] px-3 py-2 rounded text-sm font-mono-num" />
                <span className="text-xs text-[var(--muted)]">= {(value * 100).toFixed(2)}%</span>
            </div>
        </label>
    );
}

function DollarField({ label, value, onChange, locked, onLock, disabled, isOverride, onReset, placeholder, tid }) {
    return (
        <label className={`block ${disabled ? "opacity-40 pointer-events-none" : ""}`}>
            <div className="text-xs text-[var(--muted)] uppercase tracking-wider mb-1 flex justify-between items-center">
                <span>{label}</span>
                <div className="flex items-center gap-2">
                    {isOverride && onReset && (
                        <button onClick={onReset}
                                className="text-[10px] text-[var(--eucalyptus)] hover:underline">
                            auto
                        </button>
                    )}
                    {onLock && (
                        <button onClick={onLock} title={locked ? "Unlock" : "Lock"}
                                className={`hover:text-[var(--ink)] ${locked ? "text-[var(--eucalyptus)]" : "text-[var(--muted)]"}`}>
                            {locked ? <Lock size={12} weight="fill" /> : <LockOpen size={12} />}
                        </button>
                    )}
                </div>
            </div>
            <input type="number" value={value} placeholder={placeholder}
                   onChange={e => onChange(e.target.value === "" ? "" : (Number(e.target.value) || 0))}
                   data-testid={tid}
                   className="w-full border border-[var(--line)] px-3 py-2 rounded text-sm font-mono-num" />
        </label>
    );
}

function NccRow({ name, bal, suggested, capped, totalBal, tid }) {
    const share = totalBal ? (bal / totalBal) : 0.5;
    const overCap = suggested > capped + 0.01;
    return (
        <div data-testid={tid} className="p-4 rounded" style={{ background: "rgba(74,107,122,0.05)" }}>
            <div className="flex items-baseline justify-between mb-2">
                <span className="font-medium">{name}</span>
                <span className="text-xs text-[var(--muted)]">Balance share {PCT(share, 1)}</span>
            </div>
            <div className="text-xs text-[var(--muted)] mb-1">Suggested contribution</div>
            <div className="font-serif text-2xl font-mono-num">{AUD(capped)}</div>
            {overCap && (
                <div className="text-[11px] text-[var(--terracotta)] mt-1">
                    (uncapped: {AUD(suggested)} — trimmed to $120K/yr cap)
                </div>
            )}
        </div>
    );
}
